<template>
  <BaseModal :modal-name="name" max-width="max-w-[706px]">
    <p
      class="mb-10 text-2xl font-medium text-darkestGray"
      v-html="$t('patients.sign_in_to_add_other_person_data')"
    ></p>

    <div class="overflow-inherit">
      <div class="mb-6 flex flex-col">
        <!-- Tabs Section -->
        <div class="mb-4 flex flex-col items-center gap-3 sm:flex-row">
          <div
            class="group pointer-events-none flex min-h-[42px] w-full flex-row-reverse items-stretch rounded-full border border-primary text-sm font-bold hover:bg-white sm:max-w-[185px]"
          >
            <button
              v-for="(itemName, index) in menuItems"
              :key="itemName"
              class="group w-full grow rounded-full px-2 text-center font-semibold leading-none text-primary md:w-auto"
              :class="{
                'bg-primary text-white': index == selectedIndex, // Left targeted & left selected
                'hover:bg-primary hover:text-white': index == !selectedIndex, // Right targeted & not selected
                'group-hover:bg-white group-hover:text-primary':
                  index === selectedIndex, // Left or right selected
                'pointer-events-auto': index !== selectedIndex, // Left or right not selected
              }"
              @click="onChangeTab(index)"
            >
              {{ itemName }}
            </button>
          </div>
          <span v-if="selectedIndex === 0" class="text-sm text-darkestGray">
            (3 miesiące - 17 lat)
          </span>
        </div>

        <!-- Patients Information -->
        <PatientsAddPatienSection
          v-if="selectedIndex === 1"
          ref="patientFormSection"
          :is-first-patient-mode="true"
          age="adult"
        />
        <PatientsAddPatienSection
          v-if="selectedIndex === 0"
          ref="kidFormSection"
          :is-first-patient-mode="true"
          age="kid"
        />
      </div>

      <div v-if="hasPatients" class="mt-5">
        <span class="text-sm font-medium text-darkestGray">
          {{ $t('patients.up_to_5_patients_can_be_added') }}
        </span>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="gap-6 md:flex">
      <span class="text-sm text-darkestGray">
        Jeśli chcesz dodać więcej osób do zamówienia będziesz miała taką
        możliwośc w późniejszych krokach.
      </span>
      <div
        class="mt-4 flex items-center justify-center space-x-8 text-sm md:justify-end"
      >
        <BaseButton
          class="text-primary hover:underline hover:brightness-50"
          size="lg"
          theme="link"
          @click="onClickCancel"
        >
          {{ $t('action.cancel') }}
        </BaseButton>

        <BaseButton size="lg" theme="primary" @click="onClickConfirm">
          {{ $t('action.confirm') }}
        </BaseButton>
      </div>
    </div>
  </BaseModal>
</template>

<script setup>
import { useModalsStore } from '~~/stores/modals'
import { useCartStore } from '~~/stores/cart'
import { useChildrenHelper } from '~/composables/useChildrenHelper'

// Modal name
const name = 'addCurrentPatientModal'

// Store instances
const modalsStore = useModalsStore()
const cartStore = useCartStore()

// Refs and Reactive Variables
const patientFormSection = ref()
const kidFormSection = ref()
const selectedIndex = ref(1) // 0 for Child, 1 for Adult

// Menu Items for Tabs
const menuItems = ['Dziecko', 'Dorosły']

// Patients Computation (Sorted by Age)
const patients = computed(() => {
  return cartStore.patients.sort((p1, p2) => {
    if (p1.age < p2.age) return -1
    if (p1.age > p2.age) return 1
    return 0
  })
})

// Check if There Are Any Patients
const hasPatients = computed(() => {
  return patients.value.length > 0
})

// Handler for Changing Tabs
const onChangeTab = (index) => {
  selectedIndex.value = index
}

// Handler for Cancel Button
const onClickCancel = () => {
  cartStore.clearTmpPatients()
  modalsStore.close(name)
}

// Handler for Confirm Button
const onClickConfirm = async () => {
  let isValid = false

  if (selectedIndex.value === 1 && patientFormSection.value) {
    // Validate Adult Form
    isValid = await patientFormSection.value.validate()
  } else if (selectedIndex.value === 0 && kidFormSection.value) {
    // Validate Child Form and Personal Data Section
    const isKidFormValid = await kidFormSection.value.validate()

    isValid = isKidFormValid
  }

  if (isValid) {
    cartStore.saveTmpPatients()
    cartStore.assignCartToPatients()
    modalsStore.close(name)
    if (
      (!cartStore.selectedFacility?.features?.is_children ||
        cartStore.isHomeService) &&
      useChildrenHelper(cartStore.patients)
    ) {
      modalsStore.open('invalidFacilityForKidModal')
    }
  }
}
</script>

<style scoped>
/* Add any scoped styles here if necessary */
</style>
